import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Youtube } from 'lucide-react';
import { company } from '../data/mockData';

const Footer = () => {
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Products', path: '/products' },
    { name: 'Clients', path: '/clients' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <footer className="relative bg-gradient-to-b from-white to-gray-50 border-t border-cyan-500/10">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-blue-500/5 pointer-events-none" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-10 md:mb-12">
          {/* Company Info */}
          <div>
            <Link to="/" className="flex items-center mb-6 group">
              <img
                src="https://customer-assets.emergentagent.com/job_add4c97a-cab7-4cb4-a1cf-0b5112bb0652/artifacts/zur5e9w0_Stallion%20Bulktech.png"
                alt="Stallion Bulktech"
                className="h-12 w-auto object-contain transition-all duration-300 group-hover:scale-105"
              />
            </Link>
            <p className="text-sm leading-relaxed mb-6 text-gray-600">
              India's leading Industrial Machine Guards, Dust Suppression, and Wear Resistance solutions provider, engineered for reliability and reduced maintenance.
            </p>
            <div className="flex space-x-3">
              <a
                href="#"
                className="group relative p-2.5 rounded-lg transition-all"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                <Linkedin className="relative h-5 w-5 text-gray-500 group-hover:text-cyan-600 transition-colors" />
              </a>
              <a
                href="#"
                className="group relative p-2.5 rounded-lg transition-all"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                <Youtube className="relative h-5 w-5 text-gray-500 group-hover:text-cyan-600 transition-colors" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-gray-900 font-semibold text-lg mb-6">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="group text-sm text-gray-600 hover:text-cyan-600 transition-all duration-300 inline-flex items-center"
                  >
                    <span className="w-0 group-hover:w-2 h-px bg-cyan-600 mr-0 group-hover:mr-2 transition-all duration-300" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Product Categories */}
          <div>
            <h3 className="text-gray-900 font-semibold text-lg mb-6">
              Product Categories
            </h3>
            <ul className="space-y-3 text-sm">
              {[
                {
                  label: 'Machine Guards',
                  href: '/products?category=Machine Guards',
                  subcategories: [
                    { label: 'Rotating Equipment Guards', href: '/products?category=Rotating Equipment Guards' },
                    { label: 'Conveyor Guards', href: '/products?category=Conveyor Guards' },
                    { label: 'Perimeter Barrier Guards', href: '/products?category=Perimeter Barrier Guards' },
                    { label: 'Workshop Machine Guards', href: '/products?category=Workshop Machine Guards' },
                    { label: 'Smart Guards', href: '/products?category=Smart Guards' },
                    { label: 'Modular Rain Guards', href: '/products?category=Modular Rain Guards' }
                  ]
                },
                { label: 'Dust Suppression System', href: '/products?category=Dust Suppression System' },
                { label: 'Wear Resistance', href: '/products?category=Wear Resistance' }
              ].map((category) => (
                <li key={category.label}>
                  <Link
                    to={category.href}
                    className="text-gray-600 hover:text-cyan-600 transition-colors font-medium"
                  >
                    {category.label}
                  </Link>
                  {category.subcategories && (
                    <ul className="mt-2 ml-4 space-y-2 border-l border-gray-200 pl-3">
                      {category.subcategories.map((sub) => (
                        <li key={sub.label}>
                          <Link
                            to={sub.href}
                            className="text-gray-500 hover:text-cyan-600 transition-colors text-xs"
                          >
                            {sub.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-gray-900 font-semibold text-lg mb-6">
              Contact Us
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3 text-sm">
                <MapPin className="h-5 w-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">{company.address}</span>
              </li>
              <li className="flex items-start space-x-3 text-sm">
                <Phone className="h-5 w-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-1 text-gray-600">
                  <div className="hover:text-cyan-600 transition-colors cursor-pointer">{company.phone}</div>
                  <div className="hover:text-cyan-600 transition-colors cursor-pointer">{company.phone2}</div>
                </div>
              </li>
              <li className="flex items-center space-x-3 text-sm">
                <Mail className="h-5 w-5 text-cyan-600 flex-shrink-0" />
                <span className="text-gray-600 hover:text-cyan-600 transition-colors cursor-pointer">{company.email}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Certifications & Logos - Centered Below */}
        <div className="flex justify-center mb-10 md:mb-12">
          <div className="relative group max-w-xl w-full">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl blur group-hover:blur-md transition-all" />
            <div className="relative bg-white/60 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-4 sm:p-6 hover:border-cyan-500/40 transition-all shadow-lg">
              <div className="flex items-center justify-center gap-3 sm:gap-6">
                {/* ISO Certification */}
                <div className="flex-1 flex items-center justify-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_guards-premium/artifacts/p5f8nmiz_ISO%209001-2015.png"
                    alt="ISO 9001:2015 Certified"
                    className="h-12 sm:h-16 w-auto object-contain"
                  />
                </div>

                {/* Divider */}
                <div className="h-12 sm:h-16 w-px bg-gradient-to-b from-transparent via-cyan-500/30 to-transparent" />

                {/* Stallion Engineering Solutions Logo */}
                <div className="flex-1 flex items-center justify-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_add4c97a-cab7-4cb4-a1cf-0b5112bb0652/artifacts/eug16mg9_Stallion%20Engineeering%20solutions.png"
                    alt="Stallion Engineering Solutions"
                    className="h-12 sm:h-16 w-auto object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} Stallion Bulktech. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm text-gray-500">
              <Link to="/privacy-policy" className="hover:text-cyan-600 transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-cyan-600 transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
